import React, { useEffect } from 'react';

/**
 * Component to handle Amplitude Engagement features (Guides & Surveys)
 * This component should be included once in your app to enable engagement features
 * 
 * According to Amplitude docs, when using amplitude.add(engagementPlugin()),
 * no additional setup is needed - the plugin handles initialization automatically.
 */
const AmplitudeEngagement = () => {
  useEffect(() => {
    // Verify engagement is loaded (for debugging)
    if (typeof window !== 'undefined' && window.engagement) {
      console.log('Amplitude Guides & Surveys is ready');
      
      // Optional: Set theme mode if your app supports light/dark modes
      // window.engagement.setThemeMode("auto");
      
      // Optional: Configure router for SPA navigation
      // window.engagement.setRouter((newUrl) => navigate(newUrl));
    }
  }, []);

  // This component doesn't render anything visible
  return null;
};

/**
 * Official Amplitude Engagement SDK utilities
 * These use the actual SDK methods as documented
 */
export const AmplitudeEngagementUtils = {
  // Show a specific guide or survey (ignores targeting rules)
  showGuideOrSurvey: (key, stepIndex = 0) => {
    if (window.engagement?.gs?.show) {
      window.engagement.gs.show(key, stepIndex);
    }
  },

  // List all live guides and surveys
  listGuidesAndSurveys: () => {
    if (window.engagement?.gs?.list) {
      return window.engagement.gs.list();
    }
    return [];
  },

  // Reset a guide or survey to a specific step
  resetGuideOrSurvey: (key, stepIndex = 0) => {
    if (window.engagement?.gs?.reset) {
      window.engagement.gs.reset(key, stepIndex);
    }
  },

  // Close all active guides and surveys
  closeAll: () => {
    if (window.engagement?.gs?.closeAll) {
      window.engagement.gs.closeAll();
    }
  },

  // Set theme mode (lightMode, darkMode, auto)
  setThemeMode: (mode) => {
    if (window.engagement?.setThemeMode) {
      window.engagement.setThemeMode(mode);
    }
  },

  // Add a callback for guide/survey button actions
  addCallback: (name, callback) => {
    if (window.engagement?.addCallback) {
      window.engagement.addCallback(name, callback);
    }
  },

  // Forward events from third-party analytics to trigger guides/surveys
  forwardEvent: (event) => {
    if (window.engagement?.forwardEvent) {
      window.engagement.forwardEvent(event);
    }
  },

  // Set session properties for targeting
  setSessionProperty: (key, value) => {
    if (window.engagement?.setSessionProperty) {
      window.engagement.setSessionProperty(key, value);
    }
  },

  // Update language dynamically
  updateLanguage: async (locale) => {
    if (window.engagement?.updateLanguage) {
      await window.engagement.updateLanguage(locale);
    }
  },

  // Debug function to check SDK status
  getDebugStatus: () => {
    if (window.engagement?._debugStatus) {
      return window.engagement._debugStatus();
    }
    return null;
  }
};

export default AmplitudeEngagement;